import { useState, useMemo, useEffect } from "react";

import { calculateEffectivePurchase } from '../utils/utils';

import { textFormatter } from "../utils/formatter/formatter";

import { DEFAULT_VALUES } from "../components/components.constants";

import { isEligibleForPurchase } from "./strategy/purchaseOrderEligibility/withOriginalValues/withOriginalValues";

/** Máquina de estados utilizada para o fluxo de processo de compra principal 
 * Etapas, transições e lógica 
*/
const ORDER_FLOW_STEPS = Object.freeze({
  PURCHASE_FLOW: 'PURCHASE_FLOW',
  SUPPLIER_CONFIRMATION: 'SUPPLIER_CONFIRMATION',
  ORDER_CREATED: 'ORDER_CREATED',    
});

const STEP_TRANSITIONS = Object.freeze({
  [ORDER_FLOW_STEPS.PURCHASE_FLOW]: [ORDER_FLOW_STEPS.SUPPLIER_CONFIRMATION],
  [ORDER_FLOW_STEPS.SUPPLIER_CONFIRMATION]: [ORDER_FLOW_STEPS.ORDER_CREATED],
  [ORDER_FLOW_STEPS.ORDER_CREATED]: [],    
});

const getInitialStep = (appliedLevels) => {
  const hasFilter = Object.keys(appliedLevels || {}).length > 0;
  return hasFilter
    ? ORDER_FLOW_STEPS.SUPPLIER_CONFIRMATION
    : ORDER_FLOW_STEPS.PURCHASE_FLOW;
};

const resolveNextStep = (prevStep, nextStep) => {
  const allowed = STEP_TRANSITIONS[prevStep] ?? [];
  return allowed.includes(nextStep) ? nextStep : prevStep;
};

export const useSupplierConfirmationViewModel = ({ ctx, uiState }) => {

  const {
    suppliersAndProductRelations = [],
    selectedSupplierFromFilter = null,
    filteredData = [],
    totalProducts = [],
    productsWithoutSupplierCount,
  } = ctx || {};

  const { openOrderConfirmation, appliedLevels } = uiState || {};

  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [openOrderModal, setOpenOrderModal] = useState(false);
  const [orderData, setOrderData] = useState(null);

  /** Estados necessários para a máquina de estados */
  const [currentStep, setCurrentStep] = useState(null);
  const [createdOrder, setCreatedOrder] = useState(null);
  
  // funções compartilhadas
  const isEligibleForMultiple = (item) => {
    if (item.effectivePurchase > 0) return true;
    if (item.initialPurchaseRecommendation > 0) {
      return calculateEffectivePurchase(item.initialPurchaseRecommendation, item.packToUnitRatio) > 0;
    }
    return false;
  };

  // fornecedores elegíveis
  const filteredDataSuppliers = useMemo(() => {
    const supplierMap = new Map();

    filteredData?.forEach(item => {
      if (!isEligibleForMultiple(item)) return;
      item.suppliers?.forEach(supplier => {
        if (!supplierMap.has(supplier.supplierId)) {
          supplierMap.set(supplier.supplierId, {
            id: supplier.supplierId,
            name: supplier.supplierName,
            truncatedName: textFormatter(supplier.supplierName, { maxLength: 20 })
          });
        }
      });
    });

    return Array.from(supplierMap.values());
  }, [filteredData]);

  const uniqueSuppliers = useMemo(() => {
    const seen = new Map();
    const tempSuppliers = [...suppliersAndProductRelations];

    tempSuppliers.forEach(({ supplierId, supplierName }) => {
      if (!seen.has(supplierId) || !supplierName || supplierName === DEFAULT_VALUES.NOT_APPLY || seen.get(supplierId) === null) {
        if (supplierName && supplierName !== DEFAULT_VALUES.NOT_APPLY) {
          seen.set(supplierId, supplierName);
        }
      }
    });

    const result = Array.from(seen.entries()).map(([id, fullName]) => ({
      name: textFormatter(fullName, { maxLength: 20 }),
      fullName,
      id
    }));
    return result;
  }, [suppliersAndProductRelations]);

  const canProceed = useMemo(() => {

    return selectedSupplierFromFilter != null ||
      (filteredDataSuppliers.length === 1 && selectedSupplierId !== null) ||
      (filteredDataSuppliers.length > 1 && filteredDataSuppliers.length !== uniqueSuppliers.length);
    }, [
    selectedSupplierFromFilter,
    filteredDataSuppliers,
    selectedSupplierId,
    uniqueSuppliers
  ]);

  useEffect(() => {
    if (selectedSupplierFromFilter) {
      setSelectedSupplierId(selectedSupplierFromFilter.id);
      return;
    }
    
    if (filteredDataSuppliers.length === 1) {
      setSelectedSupplierId(filteredDataSuppliers[0].id);
      return;
    }

    if (uniqueSuppliers.length === 1) setSelectedSupplierId(uniqueSuppliers[0].id);
  }, [selectedSupplierFromFilter, filteredDataSuppliers, uniqueSuppliers]);

  const handleSelectChange = (e) => {
    const supplierId = parseInt(e.target.value, 10);
    setSelectedSupplierId(supplierId);
  };

  /**
   * 
   * Essa função é responsável por filtrar os produtos cuja compra final é 0. 
   * Se for zero, não serão exibidos no modal do componente em:
   * SuppliersConfirmationModal --> OrderModal
   */
  const proceedToOrderModal = () => {
    const supplierIds = uniqueSuppliers.map(s => s.id);
    const supplier = uniqueSuppliers.find(s => s.id === selectedSupplierId);

    // filtro de produtos que na UI aparecem com compra final 0
    const filteredDataToUse = filteredData.filter(item => isEligibleForPurchase(item));

    const dataToSend = {
      totalProducts,
      filteredData: filteredDataToUse,
      productsWithoutSupplierCount,
      suppliersAndProductRelations,
      supplierIds,
      selectedSupplier: supplier ? { id: supplier.id, name: supplier.fullName } : null,
      selectedSupplierId
    };

    setOrderData(dataToSend);
    setOpenOrderModal(true);
  };

  const closeOrderModal = () => {
    setOpenOrderModal(false);
    setOrderData(null);
  };

  /** Lógica para cálculo de exibição de dados de múltiplos fornecedores */
  const multipleSuppliersData = useMemo(() => {
    const filteredDataToUse = filteredData.filter(isEligibleForMultiple);

    const validSuppliers = filteredDataToUse.filter(item =>
      item.suppliers.some(s => filteredDataSuppliers.map(s => s.name).includes(s.supplierName))
    );

    const uniqueSuppliersInCurrentSelection = Array.from(
      new Map(validSuppliers.flatMap(item => item.suppliers).map(s => [s.supplierId, s])).values()
    );

    return { validSuppliers, uniqueSuppliersInCurrentSelection };
  }, [filteredData, filteredDataSuppliers]);

  // máquina de estados
  const goToStep = (nextStep) => {
    setCurrentStep((prev) => resolveNextStep(prev, nextStep));
  };

  useEffect(() => {
    if (!openOrderConfirmation) {
      setCurrentStep(null);
      setCreatedOrder(null);
      return;
    }
    setCurrentStep(getInitialStep(appliedLevels));
  }, [openOrderConfirmation, appliedLevels]);  

  return {
    actions: {
      handleSelectChange,
      proceedToOrderModal,
      closeOrderModal,
      setOpenOrderModal,
      // máquina de estados
      goToSupplierConfirmation: () => goToStep(ORDER_FLOW_STEPS.SUPPLIER_CONFIRMATION),
      goToOrderCreated: (order) => {
        setCreatedOrder(order);
        goToStep(ORDER_FLOW_STEPS.ORDER_CREATED);
      },
    },
    ctx: {
      // dados de fornecedores
      filteredDataSuppliers,
      uniqueSuppliers,
      selectedSupplierId,
      orderData,
      multipleSuppliersData,
      // máquina de estados
      createdOrder
    },
    uiState: {
      canProceed,
      openOrderModal,
      // máquina de estados
      currentStep,
      isPurchaseFlow: currentStep === ORDER_FLOW_STEPS.PURCHASE_FLOW,
      isSupplierConfirmation: currentStep === ORDER_FLOW_STEPS.SUPPLIER_CONFIRMATION,
      isOrderCreated: currentStep === ORDER_FLOW_STEPS.ORDER_CREATED,
    }
  };
};